/* eslint-disable @next/next/no-before-interactive-script-outside-document */
import config from "config/config";
import Script from "next/script";
import { useGTM } from "hooks";
import languages from "config/languages";
import type { LanguageCode } from "config/languages";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { gtmConsentModeScript, gtmScript, oneTrustTriggerScript, GTM_ID } from "./trackingScripts.config";
import type { TrackingScriptsProps } from "./TrackingScripts.d";

const getOneTrustDomainId = (language: LanguageCode) =>
  languages.get(language)?.oneTrustDomainId || config.oneTrustDomainIdCom;

export const TrackingScripts = ({ language, isTrackingless, pageReferrer }: TrackingScriptsProps) => {
  const router = useRouter();
  const isOneTrustEnabled = config.oneTrustEnabled === "enabled";
  useGTM(language, isTrackingless, pageReferrer);

  useEffect(() => {
    if (isOneTrustEnabled) {
      const reloadOTBanner = () => {
        const otConsentSdk = document.getElementById("onetrust-consent-sdk");
        if (otConsentSdk) {
          otConsentSdk.remove();
        }

        if (window.OneTrust) {
          window.OneTrust?.Init();

          setTimeout(() => {
            window.OneTrust?.LoadBanner();

            const toggleDisplay = document.getElementsByClassName(
              "ot-sdk-show-settings"
            ) as HTMLCollectionOf<HTMLElement>;

            // eslint-disable-next-line no-plusplus
            for (let i = 0; i < toggleDisplay.length; i++) {
              toggleDisplay[i].onclick = (event) => {
                event.stopImmediatePropagation();
                window.OneTrust?.ToggleInfoDisplay();
              };
            }
          }, 1000);
        }
      };

      router.events.on("routeChangeComplete", () => {
        reloadOTBanner();
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOneTrustEnabled]);

  return (
    <>
      {isOneTrustEnabled && (
        <Script
          id="gtm-consent-mode"
          strategy="beforeInteractive"
          dangerouslySetInnerHTML={{ __html: gtmConsentModeScript }}
        />
      )}
      {!isTrackingless && (
        <Script id="gtm-script" strategy="beforeInteractive" dangerouslySetInnerHTML={{ __html: gtmScript }} />
      )}
      {isOneTrustEnabled && (
        <>
          <Script
            id="one-trust-snippet"
            // data-document-language="true" // enable to get the language from the html tag
            strategy="beforeInteractive"
            src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
            charSet="UTF-8"
            data-domain-script={getOneTrustDomainId(language)}
          />
          <Script
            id="one-trust-trigger"
            strategy="beforeInteractive"
            type="text/javascript"
            dangerouslySetInnerHTML={{ __html: oneTrustTriggerScript }}
          />
        </>
      )}

      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
          height="0"
          width="0"
          title="gtm"
          style={{ display: "none", visibility: "hidden" }}
        />
      </noscript>
    </>
  );
};
